import React, { useCallback, useEffect, useMemo } from 'react'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import { useModal } from '../../components/useModal'
import { toast } from 'react-hot-toast'
import { useGetAllUsers } from '../../api/admin/useGetAllUsers'
import { User } from '../../lib/types/User'
import { motion } from 'framer-motion'
import { ReactComponent as RemoveIcon } from '../../images/icons/remove.svg'
import { useGetDealerships } from '../../api/admin/useGetDealerships'
import { useGetOrganizationByOrganizationId } from '../../api/admin/useGetAgencyByAgencyId'
import { useEditAgency } from '../../api/admin/useEditAgency'
import {
  AsyncDualSelect,
  ToggleInput,
  Tooltip,
  Button,
  RadioInput,
  TextInput,
  DualSelect,
} from '@demandlocal/components'
import { ReactComponent as ActionDelete } from '../../images/icons/trash.svg'
import { useGetTradeDeskAccounts } from '../../api/admin/useGetTradeDeskAccounts'
import { useAddVendorAccountsToOrganization } from '../../api/admin/useAddVendorAccountsToOrganization'
import { VENDOR } from '../../lib/utils/vendors'
import { isEmpty, omit } from 'lodash'
import { useGetAdminVendorAdAccounts } from '../../api/admin/useGetAdminVendorAdAccounts'
import { useSignS3LinkForUpload } from '../../api/utils/useSignS3LinkForUpload'
import { useUploadFileToSignedLink } from '../../api/utils/useUploadFileToSignedLink'
import { match } from 'ts-pattern'
import { loadStripe } from '@stripe/stripe-js'
import { queryClient } from '../../api/queryClient'

type Props = {
  agency: {
    id: number
    name: string
    created_date: string
    updated_date: string
    show_rsv: boolean
    show_budget_type: number
    has_logo: boolean
    logo?: string
  }
}
const AdminEditAgency = ({ agency }: Props): JSX.Element => {
  const { closeModal } = useModal()
  const formContext = useForm({
    mode: 'onSubmit',
  })

  const { data: agencyData, isLoading: isLoadingAgencyData } =
    useGetOrganizationByOrganizationId(agency?.id)

  const { data: assignedTradeDeskAccounts, isLoading: isLoadingVendorAdAccounts } =
    useGetAdminVendorAdAccounts({
      vendor_id: VENDOR.TradeDesk,
      agency_id: agency?.id,
    })

  const { watch, setValue, getValues } = formContext

  const assignedUsersValues = watch(`assigned_users`)
  const assignedDealershipsValues = watch(`assigned_dealerships`)
  const assignedTradeDeskAccountsValues = watch(`assigned_trade_desk_accounts`)
  const managedTradeDeskEnabled = watch('managed_ttd')
  const stripeEnabled = watch('stripe_enabled')
  const logo = watch(`logo`)

  const { data: tradeDeskAccounts } = useGetTradeDeskAccounts({})
  const tradeDeskAccountOptions = useMemo(() => {
    if (!tradeDeskAccounts?.data) return []
    return tradeDeskAccounts?.data?.map((account: { value: string; label: string }) => ({
      value: account.value,
      label: account.label,
    }))
  }, [tradeDeskAccounts])

  const { mutateAsync } = useEditAgency()
  const { mutateAsync: addVendorAccountsToOrganization } =
    useAddVendorAccountsToOrganization()

  useEffect(() => {
    if (!isLoadingAgencyData && agencyData) {
      const tradeDeskConnection = agencyData?.vendor_connections?.find(
        (connection: { vendor_id: number }) => connection.vendor_id === VENDOR.TradeDesk,
      )
      setValue('name', agencyData?.name)
      setValue('billing_email', agencyData?.billing_email)
      setValue('stripe_customer_id', agencyData?.stripe_customer_id)
      setValue('show_rsv', agencyData?.show_rsv)
      setValue('stripe_enabled', !!agencyData?.stripe_enabled)
      setValue('is_tier_2', !!agencyData?.is_tier_2)
      setValue('show_vendor_audiences', !!agencyData?.show_vendor_audiences)
      setValue(
        'show_budget_type',
        match(agencyData?.show_budget_type)
          .with(1, () => ({
            label: 'DL Net',
            value: '1',
          }))
          .with(2, () => ({
            label: 'DL Gross',
            value: '2',
          }))
          .otherwise(() => ({
            label: 'None',
            value: '0',
          })),
      )
      setValue(
        'assigned_users',
        agencyData?.assigned_users?.map((user: User) => ({
          value: user.id,
          label: `${user?.first_name ?? ''} ${user?.last_name ?? ''}`,
        })),
      )
      setValue(
        'assigned_dealerships',
        agencyData?.assigned_dealerships?.map(
          (d: { id: number; dealership: string }) => ({
            value: d.id.toString(),
            label: d.dealership,
          }),
        ),
      )
      setValue('managed_ttd', tradeDeskConnection?.is_managed)
      setValue('ttd_partner_id', tradeDeskConnection?.refresh_token)
    }
  }, [agencyData, isLoadingAgencyData, setValue])

  useEffect(() => {
    if (!isLoadingVendorAdAccounts)
      setValue('assigned_trade_desk_accounts', assignedTradeDeskAccounts)
  }, [assignedTradeDeskAccounts, isLoadingVendorAdAccounts, setValue])

  const fetchExistingLogo = useCallback(async () => {
    if (agency?.has_logo) {
      const existingLogo = await fetch(
        `https://link-logos.s3.amazonaws.com/${
          process.env.NODE_ENV !== 'production' ? 'test/' : ''
        }agency_${agency.id}`,
      )
      const data = await existingLogo.blob()
      const file = new File([data], 'logo.png')
      setValue('logo', {
        src: URL.createObjectURL(file),
        type: file.type,
        file: file,
      })
    }
  }, [agency?.has_logo, agency.id, setValue])

  useEffect(() => {
    fetchExistingLogo()
  }, [fetchExistingLogo])

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = formContext

  const { mutateAsync: createS3LinkForUpload } = useSignS3LinkForUpload()
  const { mutateAsync: uploadFileToSignedLink } = useUploadFileToSignedLink()

  const onSubmit = async (values: Partial<FieldValues>): Promise<void> => {
    const toastId = toast.loading('Saving...')
    try {
      if (values?.logo?.src) {
        const s3UploadLink = await createS3LinkForUpload({
          data: {
            mimeType: values?.logo.type,
            fileName: `${process.env.NODE_ENV !== 'production' ? 'test/' : ''}agency_${
              agency.id
            }`,
            bucket_name: 'link-logos',
          },
        })
        await uploadFileToSignedLink({
          signedRequest: s3UploadLink.uploadUrl,
          file: values?.logo.file,
        })
      }
      await mutateAsync({
        agencyId: agency.id,
        data: {
          ...omit(values, 'assigned_trade_desk_accounts'),
          has_logo: !!values?.logo?.src,
          ttd_partner_id: values?.managed_ttd ? undefined : values?.ttd_partner_id,
          managed_ttd: !!values?.managed_ttd,
          show_budget_type: parseInt(
            values?.show_budget_type?.value || values?.show_budget_type,
          ),
          stripe_enabled: !!values?.stripe_enabled,
          assigned_users: values?.assigned_users?.map(
            (user: { value: string }) => user.value,
          ),
          assigned_dealerships: values?.assigned_dealerships?.map(
            (dealership: { value: string }) => dealership.value,
          ),
        },
      })
      if (values?.managed_ttd) {
        await addVendorAccountsToOrganization({
          data: {
            vendor_id: VENDOR.TradeDesk,
            organization_id: agency.id,
            accounts: values?.assigned_trade_desk_accounts?.map(
              (account: { value: string }) => account.value,
            ),
          },
        })
      }
      queryClient.removeQueries(['admin', 'organizations', agency?.id])
      toast.success(`Organization ${values?.name} successfully updated`, {
        id: toastId,
      })
    } catch (err) {
      toast.error(`${err}`, { id: toastId })
    }
    closeModal()
  }

  return (
    <FormProvider {...formContext}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={'flex flex-1 flex-col pt-12 font-Spartan h-full'}>
        <div
          className={
            'flex flex-1 flex-col gap-4 px-12 pb-12 overflow-auto overflow-y-scroll'
          }>
          <div className={'font-bold text-blue-primary text-[28px]'}>
            Edit {agency?.name}
          </div>
          <div className={'pt-8 flex flex-col gap-5 border-b-2 pb-8'}>
            <div>
              <TextInput
                rules={{ required: 'This field is required' }}
                inverted
                placeholder={'Agency Name'}
                name={'name'}
                label={'Agency Name'}
                control={control}
              />
            </div>
            <div className="max-w-sm flex flex-col gap-4">
              <Tooltip label={'Enabling this will show RSV data on Mobile reports'}>
                <div className={'flex flex-row gap-4 items-center'}>
                  <ToggleInput name={'show_rsv'} label={'Show Walk Ins'} />
                </div>
              </Tooltip>
            </div>
            <div className="max-w-sm flex flex-col gap-4">
              <div className={'flex flex-row gap-4 items-center'}>
                <ToggleInput name={'is_tier_2'} label={'Tier 2 Agency'} />
              </div>
            </div>
            <div className="max-w-sm flex flex-col gap-4">
              <Tooltip
                label={
                  "Enabling this will activate the 'Active Audiences' tab under the Audiences page"
                }>
                <div className={'flex flex-row gap-4 items-center'}>
                  <ToggleInput
                    name={'show_vendor_audiences'}
                    label={'Show Third Party Audiences'}
                  />
                </div>
              </Tooltip>
            </div>
            <div className={'max-w-sm '}>
              <RadioInput
                label="Show budget"
                options={[
                  { label: 'None', value: '0' },
                  { label: 'DL Net', value: '1' },
                  { label: 'DL Gross', value: '2' },
                ]}
                name={'show_budget_type'}
              />
            </div>
            <ul className="list-disc list-inside">
              <li className="text-sm text-primary-xiketic">
                Budgets will Show on Campaign reports for <strong>All</strong> Agency
                Clients
              </li>
              <li className="text-sm text-primary-xiketic">
                Budgets will create Cost Per on Conversions Reports
              </li>
            </ul>
          </div>

          <div className={'pt-8 flex flex-col gap-10 border-b-2 pb-8'}>
            <div className={'max-w-sm'}>
              <Tooltip label={'Enable Stripe Payments'}>
                <div className={'flex flex-row gap-4 items-center'}>
                  <ToggleInput name={'stripe_enabled'} label={'Enable Stripe payments'} />
                </div>
              </Tooltip>
            </div>
            {stripeEnabled && (
              <div className={'grid grid-cols-2 gap-4'}>
                <TextInput
                  inverted
                  disabled={!isEmpty(agencyData?.stripe_customer_id)}
                  placeholder={'cus_xxxxxxxxxx'}
                  name={'stripe_customer_id'}
                  label={
                    <div>
                      {agencyData?.stripe_customer_id ? (
                        <a
                          href={`https://dashboard.stripe.com/customers/${agencyData?.stripe_customer_id}`}
                          target={'_blank'}
                          rel="noreferrer">
                          Stripe Customer ID
                        </a>
                      ) : (
                        'Stripe Customer ID'
                      )}
                    </div>
                  }
                  info={
                    agencyData?.stripe_customer_id
                      ? 'If you want to re-assign this customer, please contact the dev team'
                      : 'Auto-created if not provided'
                  }
                  control={control}
                />
                <TextInput
                  inverted
                  placeholder={'Billing Email'}
                  name={'billing_email'}
                  label={'Billing Email'}
                  control={control}
                />
              </div>
            )}
          </div>
          <div className={'pt-8 pb-8 border-b-2'}>
            <div className={'flex flex-row'}>
              <div className={'flex flex-col pr-5'}>
                <label
                  className={`block text-blue-primary text-sm font-Spartan font-bold mb-2`}
                  htmlFor={'assigned_dealerships'}>
                  Assign Dealerships
                </label>
                <div className={'flex flex-shrink-0 mt-4'}>
                  <AsyncDualSelect
                    rules={{ required: 'This field is required' }}
                    optionsListTitle={'Choose dealership'}
                    selectedOptionsListTitle={'Selected dealerships'}
                    buttonTitle={'+ Assign dealership'}
                    control={control}
                    fetchFn={useGetDealerships}
                    defaultFetchFnFilters={{
                      exclude: getValues('assigned_dealerships'),
                    }}
                    labelKeyName={'dealership'}
                    valueKeyName={'id'}
                    searchKeyName={'dealership'}
                    name={`assigned_dealerships`}
                  />
                </div>
              </div>
              {assignedDealershipsValues?.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className={
                    'pl-5 flex flex-row flex-1 flex-wrap justify-start items-end gap-3 border-l-2'
                  }>
                  {assignedDealershipsValues?.map(
                    (value: { value: string; label: string }, index: number) => {
                      return (
                        <div
                          key={value.value}
                          className={
                            'flex flex-col p-2 bg-white border-2 min-w-32 rounded-lg min-w-[120px]'
                          }>
                          <div className={'flex justify-between text-xs'}>
                            <span className={'font-bold'}>{value.label}</span>
                            <RemoveIcon
                              className={'cursor-pointer ml-2'}
                              onClick={(e) => {
                                e.preventDefault()
                                setValue(
                                  `assigned_dealerships`,
                                  getValues(`assigned_dealerships`)?.filter(
                                    (v: { value: string }) => v?.value !== value?.value,
                                  ),
                                )
                              }}
                            />
                          </div>
                          <span className={'font-medium pt-1 text-xs'}>
                            {value.value}
                          </span>
                        </div>
                      )
                    },
                  )}
                </motion.div>
              )}
            </div>
          </div>
          <div className={'pt-8 pb-8 border-b-2'}>
            <div className={'flex flex-row'}>
              <div className={'flex flex-col pr-5'}>
                <label
                  className={`block text-blue-primary text-sm font-Spartan font-bold mb-2`}
                  htmlFor={'assigned_dealerships'}>
                  Assign Users
                </label>
                <div className={'flex flex-shrink-0 mt-4'}>
                  <AsyncDualSelect
                    optionsListTitle={'Choose user'}
                    selectedOptionsListTitle={'Selected users'}
                    buttonTitle={'+ Assign users'}
                    control={control}
                    valueKeyName={'id'}
                    labelKeyName={['first_name', 'last_name']}
                    searchKeyName={['first_name', 'last_name']}
                    customLabelFormat={'{0} {1}'}
                    fetchFn={useGetAllUsers}
                    name={`assigned_users`}
                  />
                </div>
              </div>
              {assignedUsersValues?.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className={
                    'pl-5 flex flex-row flex-1 flex-wrap justify-start items-end gap-3 border-l-2'
                  }>
                  {assignedUsersValues?.map(
                    (value: { value: string; label: string }, index: number) => {
                      return (
                        <div
                          key={value.value}
                          className={
                            'flex flex-col p-2 bg-white border-2 min-w-32 rounded-lg min-w-[120px]'
                          }>
                          <div className={'flex justify-between text-xs'}>
                            <span className={'font-bold'}>{value.label}</span>
                            <RemoveIcon
                              className={'cursor-pointer ml-2'}
                              onClick={(e) => {
                                e.preventDefault()
                                setValue(
                                  `assigned_users`,
                                  getValues(`assigned_users`).filter(
                                    (v: { value: string }) => v.value !== value.value,
                                  ),
                                )
                              }}
                            />
                          </div>
                          <span className={'font-medium pt-1 text-xs'}>
                            {value.value}
                          </span>
                        </div>
                      )
                    },
                  )}
                </motion.div>
              )}
            </div>
          </div>
          <div className="pb-8 border-b-2">
            <div className={'font-bold text-xl'}>TradeDesk Settings</div>
            <div className={'flex flex-col pr-5 mt-4'}>
              <div className={'flex flex-row gap-4'}>
                <ToggleInput name={'managed_ttd'} label={'Enable managed TTD'} />
              </div>
            </div>
            {!managedTradeDeskEnabled && (
              <div className={'flex flex-col pr-5 mt-4'}>
                <div className={'flex flex-row gap-4'}>
                  <TextInput
                    type={'text'}
                    inverted
                    placeholder={'Partner ID (self managed)'}
                    name={'ttd_partner_id'}
                    label={'Partner ID (self managed)'}
                    control={control}
                  />
                </div>
              </div>
            )}
            {managedTradeDeskEnabled && (
              <div className={'flex flex-col pr-5 mt-4'}>
                <div className={'flex flex-row'}>
                  <div className={' pr-5'}>
                    <label
                      className={`block text-blue-primary text-sm font-Spartan font-bold mb-2`}
                      htmlFor={'assigned_dealerships'}>
                      Assign TTD accounts
                    </label>
                    <div className={'flex flex-shrink-0 mt-4'}>
                      <DualSelect
                        optionsListTitle={'Choose account'}
                        selectedOptionsListTitle={'Selected accounts'}
                        buttonTitle={'+ Assign account'}
                        control={control}
                        name={`assigned_trade_desk_accounts`}
                        options={tradeDeskAccountOptions}
                      />
                    </div>
                  </div>
                  {assignedTradeDeskAccountsValues?.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className={
                        'pl-5 flex flex-row flex-1 flex-wrap justify-start items-end gap-3 border-l-2'
                      }>
                      {assignedTradeDeskAccountsValues?.map(
                        (value: { value: string; label: string }, index: number) => {
                          return (
                            <div
                              key={value.value}
                              className={
                                'flex flex-col p-2 bg-white border-2 min-w-32 rounded-lg min-w-[120px]'
                              }>
                              <div className={'flex justify-between text-xs'}>
                                <span className={'font-bold'}>{value.label}</span>
                                <RemoveIcon
                                  className={'cursor-pointer ml-2'}
                                  onClick={(e) => {
                                    e.preventDefault()
                                    setValue(
                                      `assigned_trade_desk_accounts`,
                                      getValues(`assigned_trade_desk_accounts`).filter(
                                        (v: { value: string }) => v.value !== value.value,
                                      ),
                                    )
                                  }}
                                />
                              </div>
                              <span className={'font-medium pt-1 text-xs'}>
                                {value.value}
                              </span>
                            </div>
                          )
                        },
                      )}
                    </motion.div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <span className={'font-bold text-base'}>Logo</span>
            {!isEmpty(logo?.src) && (
              <div className={'flex flex-row items-center gap-6'}>
                {!isEmpty(logo?.src) && (
                  <img className={'object-scale-down h-24'} src={logo?.src} />
                )}
                <div className={'flex flex-row gap-2'}>
                  <Button
                    variant={'tertiary'}
                    type={'button'}
                    onClick={(e) => {
                      e.preventDefault()
                      setValue('logo', { src: '' }, { shouldDirty: true })
                    }}
                    rightSlot={
                      <ActionDelete
                        className={'fill-primary-xiketic stroke-primary-xiketic'}
                      />
                    }>
                    Delete photo
                  </Button>
                </div>
              </div>
            )}
            {isEmpty(logo?.src) && (
              <input
                type="file"
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    setValue(
                      'logo',
                      {
                        src: URL.createObjectURL(e.target.files[0]),
                        type: e.target.files[0].type,
                        file: e.target.files[0],
                      },
                      { shouldDirty: true },
                    )
                  }
                }}
              />
            )}
          </div>
        </div>
        <div className={'h-20 items-center flex justify-between border-t-2 py-4 px-12'}>
          <Button
            variant={'secondary'}
            disabled={isSubmitting}
            onClick={(e) => {
              e.preventDefault()
              closeModal()
            }}>
            Close
          </Button>
          <Button disabled={isSubmitting} type={'submit'}>
            Save
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}

export default AdminEditAgency
